{/*
  * ORTBBidRequestPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/ortb-spec/bid-request
  * Documentation > MAX Demand Partners > DSPs > ORTB Specification > Bid Request
  */}
import React, { FunctionComponent } from 'react';
import CodeSnippet from '../containers/CodeSnippetContainer';
import { DataTable, DocsPageModule } from 'UI-Dash-Commons-UI';
import styled from 'styled-components';
import {
    appObject,
    bannerObject,
    bidRequestObjectData,
    deviceObject,
    geoObject,
    impressionObject,
    metricObject,
    nativeObject,
    videoObject
} from '../redux/constants';

type ORTBBidRequestPageProps = {
    linkTargetTopPosition?: number;
}

const ORTBBidRequestPage: FunctionComponent<ORTBBidRequestPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader text={'Bid Request'} type={DocsPageModule.DocsPageHeaderType.HEADING_1}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageHeader text={'Request Objects'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'AppLovin RTB bid requests always carry a single impression (this differs from the OpenRTB schema). '
                        + 'Each request has exactly one impression object ('}/>
                <DocsPageModule.DocsPageContentCode children={'imp'}/>
                <DocsPageModule.DocsPageContentText text={').'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Seats'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'The AppLovin RTB Exchange does not support multiple seats per bidder. It ignores the seat whitelist '}/>
                <DocsPageModule.DocsPageContentCode children={'wseat'}/>
                <DocsPageModule.DocsPageContentText text={' array.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Bid Request Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DataTable
                data={bidRequestObjectData}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={bidRequestObjectData.length}
            />

            <DocsPageModule.DocsPageHeader text={'App Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DataTable
                data={appObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={appObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Device Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DataTable
                data={deviceObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={deviceObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Geo Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DataTable
                data={geoObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={geoObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Impression Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'An AppLovin RTB bid request always carries a single impression (this differs from the OpenRTB schema). '
                    + 'Each request has exactly one impression object ('}/>
                <DocsPageModule.DocsPageContentCode children={'imp'}/>
                <DocsPageModule.DocsPageContentText text={').'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'If the request includes a banner object in the impression object, the impression is eligible for HTML and MRAID ads. '
                    + 'If the request includes a video object, the impression is eligible for VAST ads. '
                    + 'The impression object may either include only a banner or video sub-object, or it may include both such objects.'}/>
            </DocsPageModule.DocsPageContent>

            <DataTable
                data={impressionObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={impressionObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Metric Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DataTable
                data={metricObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'type',
                            Header: 'Type',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                    ]
                }
                totalRowCount={metricObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Video Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DataTable
                data={videoObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={videoObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Banner Object'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DataTable
                data={bannerObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={bannerObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Native Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin native ads use '} />
                <DocsPageModule.DocsPageContentAnchor
                    to={'https://www.iab.com/wp-content/uploads/2018/03/OpenRTB-Native-Ads-Specification-Final-1.2.pdf'}
                    text={'OpenRTB Dynamic Native Ads API Specification Version 1.2'}
                    newWindow={true}
                />
                <DocsPageModule.DocsPageContentText text={'. The impression object contains the native object. The native object contains the request field with the JSON string representation of the native request. AppLovin does not support video assets at this point.'} />
            </DocsPageModule.DocsPageContent>

            <DataTable
                data={nativeObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'alwayspassed',
                            Header: 'Always Passed?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.alwayspassed}/>
                        },
                    ]
                }
                totalRowCount={nativeObject.length}
        />

            <DocsPageModule.DocsPageHeader text={'Example Bid Request'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'json',
                    code: `{
    "app": {
        "bundle": "com.example.game",
        "cat": ["IAB9-30"],
        "id": "188e1f4c72744d3da3134a25",
        "name": "Example Game",
        "publisher": {
            "id": "41c8cd761e099946e85842c11debfb4d"
        },
        "storeurl": "https://play.google.com/store/apps/details?id=com.example.game"
    },
    "at": 2,
    "bcat": ["IAB8-5","IAB8-18","IAB11-4","IAB25","IAB26"],
    "device": {
        "carrier": "telekomde",
        "connectiontype": 3,
        "devicetype": 4,
        "dnt": 0,
        "geo": {
            "city": "Barrington",
            "country": "USA",
            "metro": "",
            "type": 2,
            "zip": ""
        },
        "h": 1280,
        "hwv": "hllte",
        "ifa": "e1020eb8-fc74-454d-b420-a9763d8bc556",
        "ip": "203.0.113.12",
        "js": 1,
        "language": "de",
        "lmt": 0,
        "make": "samsung",
        "model": "SM-N7505",
        "os": "Android",
        "osv": "5.1.1",
        "ua": "Mozilla/5.0 (Linux; Android 4.3; SM-N7505 Build/JSS15J) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/40.0.2214.89 Mobile Safari/537.36",
        "w": 720
    },
    "id": "94dedeadbeefc4b8fcba406639bbf15912766bdc",
    "imp": [
        {
            "banner": {
                "api": [
                    3,
                    5
                ],
                "battr": [
                    1,
                    2,
                    5,
                    8,
                    9,
                    14,
                    17
                ],
                "btype": [
                    1,
                    4
                ],
                "h": 480,
                "pos": 1,
                "w": 320
            },
            "displaymanager": "applovin",
            "displaymanagerver": "6.1.4",
            "id": "1",
            "instl": 1,
            "secure": 0,
            "tagid": "0652dbc39a23edfd",
            "video": {
                "battr": [
                    1,
                    2,
                    3,
                    7,
                    8,
                    9,
                    14,
                    13
                ],
                "companiontype": [
                    1,
                    2,
                    3
                ],
                "h": 480,
                "linearity": 1,
                "mimes": [
                    "video/mp4"
                ],
                "protocols": [
                    2,
                    3
                ],
                "w": 320
            }
        }
    ]
}`
                } ]}
            />

            <DocsPageModule.DocsPageHeader text={'Example Native Bid Request'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'json',
                    code: `{
    "app": {
        "bundle": "com.example.game",
        "cat": [
            "IAB24"
        ],
        "id": "81a292321de8ed119cb833e0a80c4231",
        "name": "Example Game",
        "publisher": {
            "id": "41c8cd761e099946e85842c11debfb4d"
        },
        "storeurl": "https://play.google.com/store/apps/details?id=com.example.game"
    },
    "at": 2,
    "badv": [
    ],
    "device": {
        "carrier": "verizon",
        "connectiontype": 2,
        "devicetype": 4,
        "dnt": 0,
        "geo": {
            "city": "Redwood City",
            "country": "USA",
            "metro": "",
            "type": 2
        },
        "h": 0,
        "hwv": "",
        "ifa": "12341234-1234-1234-1234-123412341234",
        "ip": "255.255.255.255",
        "ipv6": "2a00:79e1:abc:6b01:d516:20e2:582d:339a",
        "js": 1,
        "language": "en",
        "lmt": 0,
        "make": "",
        "model": "",
        "os": "Android",
        "osv": "8.0.0",
        "ua": "Mozilla/5.0 (Linux; Android 8.0.0; en_US; Moto Z (2) Build/ODXS27.000-00-00) AppleWebKit/000.00 (KHTML, like Gecko) Version/4.0 Chrome/30.0.0.0 Mobile Safari/000.00",
        "w": 0
    },
    "id": "d628d2709c061c14ea4a2e17a52da1778534d1f5",
    "imp": [
        {
            "displaymanager": "applovin",
            "displaymanagerver": "6.0.0",
            "ext": { },
            "id": "1",
            "native": {
                "api": [],
                "battr": [
                    1,
                    2,
                    5,
                    8,
                    9,
                    14,
                    17,
                    3,
                    4,
                    13
                ],
                "request": "{\"native\":{\"assets\":[{\"id\":1,\"required\":1,\"title\":{\"len\":140}},{\"id\":2,\"img\":{\"hmin\":800,\"type\":3,\"wmin\":417},\"required\":1},{\"id\":3,\"img\":{\"hmin\":50,\"type\":1,\"wmin\":50},\"required\":1},{\"data\":{\"len\":140,\"type\":2},\"id\":4,\"required\":0},{\"data\":{\"len\":15,\"type\":12},\"id\":5,\"required\":1},\"context\":1501,\"plcmtcnt\":1,\"plcmttype\":1,\"ver\":\"1.2\"}}",
                "ver": "1.2"
            },
            "secure": 0,
            "tagid": "5ba489fc7ec59a27:NOTIFYCLEAN"
        }
    ]
}`
                } ]}
            />
            <DocsPageModule.DocsPageHeader text={'Example CTV Bid Request'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'json',
                    code: `{
  "id": "eb3ebe82eb5223c2525d46bd3bef213649466b0a",
  "at": 1,
  "bcat": [
    "IAB11-4",
    "IAB8-5",
    "test1",
    "IAB8-18",
    "IAB25-7",
    "IAB25-1",
    "IAB25-5",
    "IAB25-2",
    "IAB25-3",
    "IAB26-1",
    "IAB25-4",
    "IAB26-4",
    "IAB26-2",
    "IAB26-3",
    "IAB25-6",
    "test2"
  ],
  "tmax": 1000,
  "badv": [
    "test2",
    "test1"
  ],
  "app": {
    "name": "APPINT",
    "bundle": "com.applovin.appint",
    "cat": [],
    "id": "a9dad9c29f02533c9462db5555561e79",
    "publisher": {
      "id": "61310",
      "ext": {}
    },
    "content": {
      "contentrating": "TV-PG",
      "genre": "Documentary,Science",
      "episode": 2,
      "season": "7",
      "ext": {
        "channel": "AppLovin Test"
      }
    },
    "storeurl": "https://channelstore.roku.com/details/196460",
    "ext": {}
  },
  "device": {
    "ifa": "66dcd4a1-a0ed-b2b8-7e1a-3d2ea2ecc889",
    "ip": "204.14.60.221",
    "language": "en",
    "devicetype": 3,
    "js": 0,
    "geo": {
      "type": 2,
      "ipservice": 3,
      "lat": 42.2441,
      "lon": -75.2871,
      "long": -75.2871,
      "city": "Sidney Center",
      "country": "USA",
      "region": "ny",
      "dma": "502",
      "metro": "502",
      "zip": "13839",
      "ext": {
        "org": "DTC Cable",
        "isp": "DTC Cable"
      }
    },
    "ext": {},
    "make": "Roku",
    "model": "DVP",
    "os": "Roku OS",
    "dnt": 0,
    "lmt": 0
  },
  "imp": [
    {
      "id": "1",
      "displaymanager": "applovin",
      "instl": 0,
      "secure": 1,
      "tagid": "2f850a687d1acfdb:",
      "bidfloor": 22.58823529411765,
      "bidfloorcur": "USD",
      "exp": 14400,
      "video": {
        "w": 1920,
        "h": 1080,
        "battr": [
          1,
          2,
          5,
          8,
          9,
          14,
          17,
          3,
          4,
          13
        ],
        "mimes": [
          "video/mp4",
          "video/ogg",
          "video/webm"
        ],
        "placement": 1,
        "pos": 1,
        "minduration": 1,
        "maxduration": 120,
        "startdelay": -1,
        "minbitrate": 1,
        "maxbitrate": 280000,
        "linearity": 1,
        "sequence": 1,
        "protocols": [
          1,
          2,
          3,
          4,
          5,
          6
        ],
        "podid": "pod_1",
        "podseq": 0,
        "slotinpod": 0,
        "ext": {
          "maxsequence": 1
        }
      },
      "rwdd": 0,
      "ext": {}
    }
  ],
  "user": {
    "ext": {
      "gdpr": 0
    },
    "id": "66dcd4a1-a0ed-b2b8-7e1a-3d2ea2ecc889"
  },
  "regs": {
    "coppa": 0,
    "ext": {
      "ccpa": 0,
      "gdpr": 0
    }
  },
  "source": {
    "ext": {
      "schain": {
        "ver": "1.0",
        "complete": 1,
        "nodes": [
          {
            "asi": "applovin.com",
            "sid": "61310",
            "rid": "eb3ebe82eb5223c2525d46bd3bef213649466b0a",
            "hp": 1
          }
        ]
      }
    }
  }
}`
                } ]}
            />

        </>
    )
}

const DescriptionCellWrapper = styled( DocsPageModule.DocsPageContent )`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: normal;
`;

export default ORTBBidRequestPage;
